import { msg } from "@lingui/core/macro";
import { Trans, useLingui } from "@lingui/react/macro";
import type { ReactNode } from "react";

import { formatPercent, useRankLookup } from "@/features/matches";

import { BlockError } from "@/shared/ui/block-error";
import { RefreshBar } from "@/shared/ui/refresh";
import { Skeleton } from "@/shared/ui/skeleton";
import { Spinner } from "@/shared/ui/spinner";

import { useHeroBuilds } from "../../api/useHeroBuilds";
import { BUILD_KIND_LABEL } from "../../model/BUILD_KIND_LABEL";
import { SHOWN_BUILDS } from "../../model/SHOWN_BUILDS";
import { FindBuildHint } from "../FindBuildHint";
import { HeroOverview } from "../HeroOverview";
import type { HeroChoice } from "./HeroPicker";
import { CatalogBuildCard } from "./CatalogBuildCard";
import { KindBar } from "./KindBar";

const MATCHES_LINE = msg({
  id: "builds.matches_line",
  message:
    "{count, plural, one {# match of strong players with builds from the game} other {# matches of strong players with builds from the game}}",
});

// Без «сильных»: кто они, сказано рядом рангом (доска «19е», BuildsStrongOnce.dc.html).
const MATCHES_LINE_SHORT = msg({
  id: "builds.matches_line_short",
  message:
    "{count, plural, one {# match with builds from the game} other {# matches with builds from the game}}",
});

const HERO_MATCHES_SHORT = msg({
  id: "builds.hero_matches_short",
  message: "{count, plural, one {# match over 30 days} other {# matches over 30 days}}",
});

const HERO_MATCHES_LINE = msg({
  id: "builds.hero_matches_line",
  message:
    "{count, plural, one {# match of strong players over 30 days} other {# matches of strong players over 30 days}}",
});

const IN_GAME_TITLE = msg({ id: "builds.in_game.title", message: "Ready builds from the game" });
const IN_GAME_HINT = msg({
  id: "builds.in_game.hint",
  message: "Subscribe to a build in the game and the shop will suggest what to buy next",
});

const NO_BUILDS = msg({
  id: "builds.empty",
  message: "Strong {hero} players do not play with builds from the game browser yet",
});

type HeroBuildsViewProps = {
  hero: HeroChoice;
};

// Страница героя: что собирают все сильные на нём (обзор, доска «19д»), ниже — сборки из игры:
// доли типов, как найти сборку в игре и лучшие сборки каждого типа. Каталог собран до обзора —
// только сборки. Каталога героя ещё нет (новый герой, первый сбор) — шаг со спиннером.
export const HeroBuildsView = ({ hero }: HeroBuildsViewProps) => {
  const { i18n } = useLingui();
  const rankLookup = useRankLookup();
  const builds = useHeroBuilds(hero.id);
  const refreshing = builds.isFetching && builds.isPlaceholderData;

  const header = (line: ReactNode) => (
    <div className="flex flex-wrap items-center gap-x-4 gap-y-3">
      {hero.iconUrl !== null && (
        <img
          src={hero.iconUrl}
          alt=""
          width={56}
          height={56}
          className="size-14 rounded-[10px] bg-surface-raised object-cover"
        />
      )}
      <div className="flex min-w-0 flex-[1_1_280px] flex-col gap-0.5">
        <h2 className="font-display text-[30px] leading-tight">{hero.name}</h2>
        <div className="text-[13px] text-subtle">{line}</div>
      </div>
    </div>
  );

  if (builds.isError) {
    return (
      <section className="flex flex-col gap-5">
        {header(null)}
        <BlockError
          title={<Trans>Could not load the builds</Trans>}
          retrying={builds.isFetching}
          onRetry={() => void builds.refetch()}
        />
      </section>
    );
  }
  const data = builds.data;
  if (data === undefined || data.status === "pending" || data.catalog === null) {
    return (
      <section aria-busy="true" className="flex flex-col gap-5">
        {header(null)}
        <p className="flex items-center gap-2 text-sm text-muted">
          <Spinner className="size-4" />
          <Trans>
            Builds of this hero are still being collected — they will appear within an hour
          </Trans>
        </p>
        {Array.from({ length: SHOWN_BUILDS }, (_, index) => (
          <Skeleton key={index} className="h-[132px] w-full rounded-xl" />
        ))}
      </section>
    );
  }
  const catalog = data.catalog;
  const overview = catalog.overview ?? null;
  const noBuilds = (
    <p className="rounded-[10px] border border-dashed border-border p-6 text-center text-muted">
      {i18n._({ ...NO_BUILDS, values: { hero: hero.name } })}
    </p>
  );
  if (catalog.groups.length === 0 && overview === null) {
    return (
      <section className="flex flex-col gap-5">
        {header(null)}
        {noBuilds}
      </section>
    );
  }
  const buildsLine = i18n._({ ...MATCHES_LINE, values: { count: catalog.matches } });
  // Кто «сильные» — один раз под героем: матчи за 30 дней и ранг со значком. Дальше подписи
  // слово не повторяют. Ранга нет (порог не задан) — прежняя строка «матчей сильных игроков».
  const minBadge = data.min_badge ?? null;
  const rank = minBadge === null ? null : rankLookup(minBadge);
  const rankName = rank?.name;
  const heroMatches = (count: number) => {
    if (rank === null) {
      return i18n._({ ...HERO_MATCHES_LINE, values: { count } });
    }
    const matchesText = i18n._({ ...HERO_MATCHES_SHORT, values: { count } });
    return (
      <span data-min-badge={minBadge ?? undefined} className="flex items-center gap-2">
        {rank.iconUrl !== null && (
          <img src={rank.iconUrl} alt="" className="size-[22px] shrink-0 object-contain" />
        )}
        <span>
          <Trans id="builds.hero_matches_rank">
            {matchesText}, rank <b className="font-semibold text-foreground">{rankName}</b> and
            above
          </Trans>
        </span>
      </span>
    );
  };
  return (
    <section
      aria-label={hero.name}
      aria-busy={refreshing || undefined}
      className="relative flex flex-col gap-5"
    >
      <RefreshBar active={refreshing} />
      {header(overview === null ? buildsLine : heroMatches(overview.matches))}
      {overview !== null && (
        <HeroOverview overview={overview} purposes={data.item_purposes ?? []} hero={hero.name} />
      )}
      {overview !== null && (
        <div className="flex flex-col gap-0.5 border-t border-surface-raised pt-5">
          <h3 className="text-xl font-semibold">{i18n._(IN_GAME_TITLE)}</h3>
          <span className="text-[13px] text-subtle">{i18n._(IN_GAME_HINT)}</span>
        </div>
      )}
      {catalog.groups.length === 0 ? (
        noBuilds
      ) : (
        <>
          <KindBar groups={catalog.groups} total={catalog.matches} />
          {overview !== null && (
            <span className="text-xs text-subtle">
              {i18n._({ ...MATCHES_LINE_SHORT, values: { count: catalog.matches } })}
            </span>
          )}
          <FindBuildHint />
          {catalog.groups.map((group) => {
            const label = i18n._({ ...BUILD_KIND_LABEL, values: { kind: group.kind } });
            const share = formatPercent(group.matches / catalog.matches, i18n.locale);
            return (
              <div key={group.kind} className="flex flex-col gap-2.5">
                <h3 className="flex items-baseline gap-2 text-lg font-semibold">
                  {label}
                  <span className="font-mono text-[15px] font-medium text-subtle">{share}</span>
                </h3>
                <ol className="flex flex-col gap-2.5">
                  {group.builds.slice(0, SHOWN_BUILDS).map((build, index) => (
                    <CatalogBuildCard key={build.build_id} build={build} rank={index + 1} />
                  ))}
                </ol>
              </div>
            );
          })}
          <p className="text-xs text-subtle">
            <Trans id="builds.footnote">
              Over 30 days · time is when the item is bought with this build
            </Trans>
          </p>
        </>
      )}
    </section>
  );
};
